import {
  Task,
  Update,
  defModule,
  type TaskDeclaration,
  type TaskMessage,
  type UpdateResult
} from '@st/redux'
import type { FolderImportPreview, SDKOperationFor, SDKRequestFor } from '@st/sdk'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { Analytics, SdkClient } from '~/effects'
import { observeRoute, runSdkRequest } from '~/features/module-runtime'
import {
  FOLDER_IMPORT_POLL_INTERVAL,
  isFolderImportReady,
  selDefaultJobIds
} from '~/features/folder-import/folder-import-state'

type ClientPacketsSession = {
  importId: string
}

type LoadedImport = ClientPacketsSession & {
  importState: FolderImportPreview
}

type FinishingExport = LoadedImport & {
  step: 'finishing'
  nextImportId: string | null
}

export type ClientPacketsState =
  | { step: 'idle' }
  | (ClientPacketsSession & { step: 'loading' })
  | (LoadedImport & { step: 'ready' })
  | (LoadedImport & { step: 'exporting' })
  | FinishingExport
  | (LoadedImport & {
      step: 'exported'
      exportResult: ExportClientPacketsResult
    })

type ExportClientPacketsResult = SDKOperationFor<'folders/exportClientPackets'>['response']

const observeClientPacketsRoute = observeRoute({ name: 'client_packets_export' })

const observeClientPacketsImport = Task.define((importId: string) =>
  Task.pipe(
    runSdkRequest(fetchImportStateEffect(importId)),
    Task.map((result) => {
      if (!result.ok) return null
      return {
        type: 'importStateLoaded',
        importState: result.response
      } as const
    }),
    // The import is an explicitly polled resource. A transient interruption
    // skips one sample; the next period remains the authoritative retry.
    Task.periodic(FOLDER_IMPORT_POLL_INTERVAL)
  )
)

export type ClientPacketsMessage =
  | TaskMessage<typeof observeClientPacketsRoute>
  | TaskMessage<typeof observeClientPacketsImport>
  | { type: 'exportPackets' }
  | {
      type: 'packetExportSettled'
      result: { status: 'exported'; exportResult: ExportClientPacketsResult } | { status: 'failed' }
    }

/**
 * Client-packet export topology:
 *
 * idle --matching route--> loading --import ready--> ready
 *  ^                         |                       |
 *  +----- route leaves -----+                       | export
 *                                                    v
 *                             ready <--failed-- exporting --success--> exported
 *
 * Polling exists only while `loading`, so leaving the route cancels that read.
 * Packet generation starts by durably creating folders. Once accepted, it is
 * a Command: leaving the route hides the wizard but cannot cancel half of the
 * write/export sequence. `finishing` reserves that single accepted operation;
 * no request identity is copied through the message protocol.
 */
export const clientPacketsModule = defModule<{
  state: ClientPacketsState
  message: ClientPacketsMessage
}>({
  init: () => ({ step: 'idle' }),
  tasks: clientPacketsTasks,
  update: pipe(
    updateClientPackets,
    Update.ignoreWhen<ClientPacketsState, ClientPacketsMessage>(
      isMatching({ state: { step: 'exporting' }, message: { type: 'exportPackets' } })
    )
  )
})

function clientPacketsTasks(state: ClientPacketsState): TaskDeclaration<ClientPacketsMessage>[] {
  const tasks: TaskDeclaration<ClientPacketsMessage>[] = [observeClientPacketsRoute()]

  return match(state)
    .returnType<TaskDeclaration<ClientPacketsMessage>[]>()
    .with({ step: 'loading' }, ({ importId }) => [...tasks, observeClientPacketsImport(importId)])
    .with({ step: P.union('idle', 'ready', 'exporting', 'finishing', 'exported') }, () => tasks)
    .exhaustive()
}

function updateClientPackets(
  state: ClientPacketsState,
  message: ClientPacketsMessage
): UpdateResult<ClientPacketsState, ClientPacketsMessage> {
  return match({ state, message })
    .returnType<UpdateResult<ClientPacketsState, ClientPacketsMessage>>()
    .with({ message: { type: 'routeChanged' } }, ({ state, message }) => {
      if (state.step == 'exporting') {
        if (message.route?.importId == state.importId) return state
        return {
          ...state,
          step: 'finishing',
          nextImportId: message.route?.importId ?? null
        }
      }
      if (state.step == 'finishing') {
        if (message.route?.importId == state.importId) {
          const { nextImportId: _, ...exporting } = state
          return { ...exporting, step: 'exporting' }
        }
        return { ...state, nextImportId: message.route?.importId ?? null }
      }
      if (!message.route) return { step: 'idle' }
      if (state.step != 'idle' && state.importId == message.route.importId) return state
      return { step: 'loading', importId: message.route.importId }
    })
    .with(
      { state: { step: 'loading' }, message: { type: 'importStateLoaded' } },
      ({ state, message }) => {
        if (!isFolderImportReady(message.importState)) return state
        return {
          step: 'ready',
          importId: state.importId,
          importState: message.importState
        }
      }
    )
    .with({ state: { step: 'ready' }, message: { type: 'exportPackets' } }, ({ state }) => {
      const jobIds = selClientPacketJobIds(state.importState)
      if (jobIds.length == 0) return state
      return [{ ...state, step: 'exporting' }, exportClientPacketsEffect(state.importId, jobIds)]
    })
    .with(
      {
        state: { step: 'exporting' },
        message: { type: 'packetExportSettled', result: { status: 'exported' } }
      },
      ({ state, message }) => ({
        ...state,
        step: 'exported',
        exportResult: message.result.exportResult
      })
    )
    .with(
      {
        state: { step: 'exporting' },
        message: { type: 'packetExportSettled', result: { status: 'failed' } }
      },
      ({ state }) => ({ ...state, step: 'ready' })
    )
    .with(
      { state: { step: 'finishing' }, message: { type: 'packetExportSettled' } },
      ({ state }) => {
        if (state.nextImportId) return { step: 'loading', importId: state.nextImportId }
        return { step: 'idle' }
      }
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

export function selClientPacketJobIds(importState: FolderImportPreview) {
  return selDefaultJobIds(importState.folders)
}

function fetchImportStateEffect(importId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    return yield* sdk.send({
      type: 'folderGeneration/getFolderImportPreview',
      taxReturnImportId: importId
    } satisfies SDKRequestFor<'folderGeneration/getFolderImportPreview'>)
  })
}

function exportClientPacketsEffect(importId: string, jobIds: string[]) {
  const exportPackets = Effect.gen(function* () {
    const sdk = yield* SdkClient

    yield* sdk.send({
      type: 'folderGeneration/createFolders',
      taxReturnImportId: importId,
      taxReturnIds: jobIds
    } satisfies SDKRequestFor<'folderGeneration/createFolders'>)

    const result = yield* sdk.send({
      type: 'folders/exportClientPackets',
      importJobId: importId
    } satisfies SDKRequestFor<'folders/exportClientPackets'>)

    const analytics = yield* Analytics
    yield* analytics.track('client_packets_exported', {
      import_job_id: importId
    })

    return {
      type: 'packetExportSettled',
      result: { status: 'exported', exportResult: result }
    } as const
  })

  return Effect.gen(function* () {
    const result = yield* runSdkRequest(exportPackets)
    if (!result.ok) {
      return { type: 'packetExportSettled', result: { status: 'failed' } } as const
    }
    return result.response
  })
}

function unexpectedMessage(state: ClientPacketsState, message: ClientPacketsMessage): never {
  throw new Error(`Unexpected client packets message ${message.type} while ${state.step}`)
}
